import type { ClaimStatus, PaymentStatus } from './types.js';

/**
 * Internal status values double as display labels. The prototype deliberately
 * spells out "Pending X" so a requestor can always tell which desk a claim is on.
 */
export const STATUS_LABEL: Record<ClaimStatus, string> = {
  'For Checking': 'Pending Manager Approval',
  'For Approval': 'Pending Lead Approval',
  Disapproved: 'Disapproved / For Resubmission',
  'For Finance Review': 'Pending Finance Review and Approval',
  'Finance Reviewing': 'Finance Review and Approval (in progress)',
  Released: 'Released / Completed',
  Voided: 'Voided by Administrator',
};

/**
 * Labels for statuses Round 2 retired.
 *
 * Nothing is written with these any more, but the audit trail quotes them and
 * a claim restored from an old backup could still carry one. Rendering the raw
 * value would be honest; rendering it with "(retired)" is honest and tells the
 * reader why they have never seen it.
 */
const RETIRED_STATUS_LABEL: Record<string, string> = {
  'For Finance Approval': 'Pending Finance Approval (retired stage)',
  'Finance Approving': 'Finance Approval in progress (retired stage)',
  'For Treasury Release': 'Pending Treasury Release (retired stage)',
  'Treasury Processing': 'Treasury Release in progress (retired stage)',
};

export function statusLabel(status: ClaimStatus | string): string {
  return STATUS_LABEL[status as ClaimStatus] ?? RETIRED_STATUS_LABEL[status] ?? status;
}

const BADGE_BY_STATUS: Record<string, string> = {
  'For Checking': 'badge-checking',
  'For Approval': 'badge-approval',
  Disapproved: 'badge-disapproved',
  'For Finance Review': 'badge-finance',
  'Finance Reviewing': 'badge-processing',
  Released: 'badge-paid',
  Voided: 'badge-disapproved',
  // Retired by Round 2; kept so a historic value still renders as a badge.
  'For Finance Approval': 'badge-finance',
  'Finance Approving': 'badge-processing',
  'For Treasury Release': 'badge-finance',
  'Treasury Processing': 'badge-processing',
};

export function statusBadgeClass(status: ClaimStatus | string): string {
  return BADGE_BY_STATUS[status as ClaimStatus] ?? 'badge-draft';
}

export function paymentBadgeClass(status: PaymentStatus | string): string {
  return (
    { Pending: 'badge-bir-for', Released: 'badge-bir-done', Failed: 'badge-disapproved' }[
      status as PaymentStatus
    ] ?? 'badge-bir-none'
  );
}

/**
 * Statuses that sit on a Finance desk.
 *
 * One stage since Round 2, but still a list: it is used as a SQL array and
 * reads the same at every call site whether it holds one status or six.
 */
export const IN_FINANCE_PIPELINE: ClaimStatus[] = ['For Finance Review', 'Finance Reviewing'];

/** A claim in a terminal state accepts no further workflow transitions. */
export function isTerminal(status: ClaimStatus): boolean {
  return status === 'Released' || status === 'Voided';
}

/**
 * Stages an administrator may reassign the current owner of.
 *
 * One entry since Round 2 merged the finance desks. A claim is only reassignable
 * once somebody has taken it — an unowned claim in a shared queue has no owner
 * to hand over.
 */
export const OWNED_STAGES: Record<string, 'financeReviewerId'> = {
  'Finance Reviewing': 'financeReviewerId',
};
